import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal,
  Pressable,
  ActivityIndicator,
  TextInput,
  FlatList,
  RefreshControl,
  Platform,
  useWindowDimensions,
} from "react-native";
import FeatherIcon from "react-native-vector-icons/Feather";
import { useDispatch, useSelector } from "react-redux";
import { useIsFocused } from "@react-navigation/native";
import { ThemeProvider, useTheme as usePaperTheme } from "react-native-paper";
import { DatePickerModal, registerTranslation } from "react-native-paper-dates";
import { useAppTheme } from "../../../../hooks/useAppTheme";
import { ThemeColors } from "../../../../utils/constants/colors";
import { View } from "../../../../components/shared/Themed";
import statusColorCodes from "../../../../utils/constants/statusColorCodes";
import { formatValueAsDataType } from "../../../../utils/common";
import { getOrganizationOrders } from "../../../../redux/dispatcher/portalDispatcher";
import {
  resetOrderQuery,
  setOrderCategoryFilter,
  setOrderDateFilter,
  setOrderPage,
  setOrderSearchQuery,
  setRequestType,
} from "../../../../redux/reducers/portalReducer";

registerTranslation("en-AU", {
  save: "Save",
  selectSingle: "Select date",
  selectMultiple: "Select dates",
  selectRange: "Select period",
  notAccordingToDateFormat: (inputFormat) =>
    `Date format must be ${inputFormat}`,
  mustBeHigherThan: (date) => `Must be later than ${date}`,
  mustBeLowerThan: (date) => `Must be earlier than ${date}`,
  mustBeBetween: (startDate, endDate) =>
    `Must be between ${startDate} - ${endDate}`,
  dateIsDisabled: "Day is not allowed",
  previous: "Previous",
  next: "Next",
  typeInDate: "Type in date",
  pickDateFromCalendar: "Pick date from calendar",
  close: "Close",
  hour: "Hour",
  minute: "Minute",
  startDate: "Start Date (DD/MM/YYYY)",
  endDate: "End Date (DD/MM/YYYY)",
});

// ─── Design Tokens (same as the organizations list) ──────────────────────────
const getC = (colors: ThemeColors, isDark: boolean) => ({
  navy: isDark ? colors.surface : "#0F1B2D",
  accent: colors.primary,
  success: colors.success,
  successBg: isDark ? "rgba(14, 159, 110, 0.2)" : "#EDFAF4",
  danger: colors.danger,
  dangerBg: isDark ? "rgba(231, 76, 60, 0.2)" : "#FEF0EE",
  violet: isDark ? "#A78BFA" : "#7C3AED",
  surface: colors.surface,
  surfaceAlt: isDark ? colors.surfaceSubtle : "#F5F7FB",
  border: colors.borderSubtle,
  borderLight: colors.borderSubtle,
  text: colors.text,
  textMid: colors.textSecondary,
  textMuted: colors.textMuted,
  overlay: "rgba(10,20,35,0.5)",
});

type Tokens = ReturnType<typeof getC>;

const useTokens = () => {
  const { colors, isDark } = useAppTheme();
  return useMemo(() => getC(colors, isDark), [colors, isDark]);
};

const WIDE_BREAKPOINT = 768;
const CONTENT_MAX_WIDTH = 1280;
const webNoOutline =
  Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null;

// ─── Filters ──────────────────────────────────────────────────────────────────

const DATE_OPTIONS = [
  "Today",
  "Yesterday",
  "Last 7 days",
  "Bi-Weekly",
  "Next 7 days",
  "Next 14 days",
  "Next 30 days",
];
const REQUEST_TYPES = ["Request Delivery", "Made To Order", "Pick up"];
// Same values as the backend's ORDER_STATUSES — these are what get filtered on
const STATUSES = Object.freeze([
  "Pending For Approval",
  "Pending For Paperwork",
  "Cancelled",
  "Ready",
  "Shipped",
  "Back-Order",
]) as readonly string[];

/** Display-only names; the backend value is unchanged */
const STATUS_LABELS: Record<string, string> = {
  "Pending For Paperwork": "Progressing",
};
const statusLabel = (status?: string | null) =>
  (status && STATUS_LABELS[status]) || status || "";

type FilterKey = "date" | "requestType" | "status";

/** Redux stores multi-selects as "a,b" or "all". */
const toList = (v?: string | null) =>
  !v || v === "all" ? [] : v.split(",").filter(Boolean);
const fromList = (list: string[]) => (list.length ? list.join(",") : "all");

const formatLocalDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const dateLabel = (v?: string | null) => {
  if (!v || v === "all") return "Any time";
  if (v.startsWith("Custom:")) {
    const [from, to] = v.replace("Custom:", "").split("_");
    const fmt = (s: string) => s.split("-").reverse().join("/");
    return from === to ? fmt(from) : `${fmt(from)} – ${fmt(to)}`;
  }
  return v;
};

const multiLabel = (
  list: string[],
  all: string,
  label: (v: string) => string = (v) => v,
) =>
  list.length === 0
    ? all
    : list.length === 1
      ? label(list[0])
      : `${list.length} selected`;

// ─── Order helpers ────────────────────────────────────────────────────────────

interface Order {
  _id?: string;
  orderNo?: string;
  docNumbers?: string[];
  documentNumber?: string;
  orderStatus?: string;
  requestType?: string;
  createdByEmail?: string | null;
  /** Set when a sales rep placed the order (id, or populated { _id, email }) */
  createdBySales?: string | { _id?: string; email?: string | null } | null;
  createdBySalesEmail?: string | null;
  /** Set when a super admin placed the order */
  systemGenerated?: boolean;
  createdAt?: string;
  subOrganization?: { _id: string; name: string } | null;
}

type CreatorKind = "sales" | "superadmin" | "customer";

/** Who placed the order — same rules as the customer-side order list */
const creatorOf = (o: Order): { kind: CreatorKind; email: string | null } => {
  const sales = o.createdBySales;
  if (sales) {
    const email =
      (typeof sales === "object" ? sales.email : null) ??
      o.createdBySalesEmail ??
      o.createdByEmail ??
      null;
    return { kind: "sales", email };
  }
  if (o.systemGenerated) {
    return { kind: "superadmin", email: o.createdByEmail ?? null };
  }
  return { kind: "customer", email: o.createdByEmail ?? null };
};

const CREATOR_TAG: Record<
  Exclude<CreatorKind, "customer">,
  { label: string; icon: string; color: (C: Tokens) => string }
> = {
  sales: { label: "Sales", icon: "briefcase", color: (C) => C.accent },
  superadmin: { label: "Super admin", icon: "shield", color: (C) => C.violet },
};

const orderTitle = (o: Order) =>
  o.orderNo || o.docNumbers?.join(", ") || o.documentNumber || "—";

const orderDocs = (o: Order) =>
  o.orderNo && o.docNumbers?.length ? o.docNumbers.join(", ") : null;

const statusColor = (status: string | undefined, C: Tokens) =>
  (status && (statusColorCodes as Record<string, string>)[status]) ||
  C.textMuted;

const formatDate = (v?: string) => (v ? formatValueAsDataType(v, false) : "—");

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

// ─── Small pieces ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status, C }: { status?: string; C: Tokens }) => {
  if (!status) return null;
  const color = statusColor(status, C);
  return (
    <RNView
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        alignSelf: "flex-start",
        maxWidth: "100%",
        paddingHorizontal: 9,
        paddingVertical: 4,
        borderRadius: 999,
        backgroundColor: color + "1A",
      }}
    >
      <RNView
        style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: color }}
      />
      <Text
        style={{ fontSize: 11, fontWeight: "700", color, flexShrink: 1 }}
        numberOfLines={1}
      >
        {statusLabel(status)}
      </Text>
    </RNView>
  );
};

const Meta = ({
  icon,
  text,
  C,
}: {
  icon: string;
  text?: string | null;
  C: Tokens;
}) =>
  text ? (
    <RNView
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        flexShrink: 1,
      }}
    >
      <FeatherIcon name={icon} size={12} color={C.textMuted} />
      <Text
        style={{
          fontSize: 12,
          color: C.textMid,
          fontWeight: "600",
          flexShrink: 1,
        }}
        numberOfLines={1}
      >
        {text}
      </Text>
    </RNView>
  ) : null;

/** "Created by" — a Sales / Super admin pill plus the email */
const CreatedBy = ({
  order,
  compact,
  C,
}: {
  order: Order;
  /** Card layout: icon + one line */
  compact?: boolean;
  C: Tokens;
}) => {
  const { kind, email } = creatorOf(order);
  const tag = kind === "customer" ? null : CREATOR_TAG[kind];

  if (!tag) {
    return compact ? (
      <Meta icon="user" text={email} C={C} />
    ) : (
      <Text style={{ fontSize: 13, color: C.textMid }} numberOfLines={1}>
        {email || "—"}
      </Text>
    );
  }

  const color = tag.color(C);
  const pill = (
    <RNView
      accessibilityLabel={`Created by ${tag.label}`}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        alignSelf: "flex-start",
        paddingHorizontal: 7,
        paddingVertical: 2,
        borderRadius: 6,
        backgroundColor: color + "1A",
      }}
    >
      <FeatherIcon name={tag.icon} size={10} color={color} />
      <Text style={{ fontSize: 10, fontWeight: "800", color }}>
        {tag.label.toUpperCase()}
      </Text>
    </RNView>
  );

  if (compact) {
    return (
      <RNView
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          flexShrink: 1,
        }}
      >
        {pill}
        {!!email && (
          <Text
            style={{
              fontSize: 12,
              color: C.textMid,
              fontWeight: "600",
              flexShrink: 1,
            }}
            numberOfLines={1}
          >
            {email}
          </Text>
        )}
      </RNView>
    );
  }

  return (
    <RNView style={{ gap: 3, minWidth: 0 }}>
      {pill}
      {!!email && (
        <Text style={{ fontSize: 12, color: C.textMid }} numberOfLines={1}>
          {email}
        </Text>
      )}
    </RNView>
  );
};

// Bottom sheet on phones, centred dialog on wide screens
const ResponsiveSheet: React.FC<{
  visible: boolean;
  onClose: () => void;
  maxWidth?: number;
  children: React.ReactNode;
}> = ({ visible, onClose, maxWidth = 400, children }) => {
  const C = useTokens();
  const RS = useMemo(() => createRS(C), [C]);
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  return (
    <Modal
      visible={visible}
      transparent
      animationType={wide ? "fade" : "slide"}
      onRequestClose={onClose}
    >
      <RNView
        style={[RS.backdrop, wide ? RS.backdropCenter : RS.backdropBottom]}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityLabel="Close"
        />
        <RNView
          style={[
            RS.panel,
            wide ? [RS.panelDialog, { maxWidth }] : RS.panelSheet,
          ]}
        >
          {!wide && <RNView style={RS.handle} />}
          {children}
        </RNView>
      </RNView>
    </Modal>
  );
};

const CloseButton = ({ onPress, C }: { onPress: () => void; C: Tokens }) => (
  <Pressable
    onPress={onPress}
    hitSlop={8}
    accessibilityLabel="Close"
    style={(s: any) => ({
      width: 32,
      height: 32,
      borderRadius: 8,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: s.hovered || s.pressed ? C.surfaceAlt : "transparent",
    })}
  >
    <FeatherIcon name="x" size={18} color={C.textMuted} />
  </Pressable>
);

// ─── Filter sheet ─────────────────────────────────────────────────────────────

interface FilterSheetProps {
  visible: boolean;
  title: string;
  options: readonly string[];
  selected: string[];
  multi: boolean;
  /** Display name for an option (the value passed to onChange is unchanged) */
  labelOf?: (value: string) => string;
  allLabel: string;
  onChange: (next: string[]) => void;
  onClose: () => void;
  /** Extra row under the options (e.g. "Custom range…") */
  footer?: React.ReactNode;
}

const FilterSheet: React.FC<FilterSheetProps> = ({
  visible,
  title,
  options,
  selected,
  multi,
  allLabel,
  onChange,
  onClose,
  footer,
  labelOf = (v) => v,
}) => {
  const C = useTokens();
  const FS = useMemo(() => createFS(C), [C]);

  const toggle = (opt: string) => {
    if (!multi) {
      onChange(selected.includes(opt) ? [] : [opt]);
      onClose();
      return;
    }
    onChange(
      selected.includes(opt)
        ? selected.filter((s) => s !== opt)
        : [...selected, opt],
    );
  };

  const Row = ({
    label,
    on,
    onPress,
  }: {
    label: string;
    on: boolean;
    onPress: () => void;
  }) => (
    <Pressable
      onPress={onPress}
      accessibilityRole={multi ? "checkbox" : "radio"}
      accessibilityState={{ checked: on }}
      style={(s: any) => [
        FS.option,
        (s.hovered || s.pressed) && FS.optionHover,
        on && FS.optionActive,
      ]}
    >
      <RNView style={[multi ? FS.checkbox : FS.radio, on && FS.markOn]}>
        {on &&
          (multi ? (
            <FeatherIcon name="check" size={12} color="#fff" />
          ) : (
            <RNView style={FS.radioDot} />
          ))}
      </RNView>
      <Text style={[FS.optionText, on && FS.optionTextActive]}>{label}</Text>
    </Pressable>
  );

  return (
    <ResponsiveSheet visible={visible} onClose={onClose}>
      <RNView style={FS.titleRow}>
        <Text style={FS.title}>{title}</Text>
        <CloseButton onPress={onClose} C={C} />
      </RNView>
      <Row
        label={allLabel}
        on={selected.length === 0}
        onPress={() => {
          onChange([]);
          if (!multi) onClose();
        }}
      />
      {options.map((opt) => (
        <Row
          key={opt}
          label={labelOf(opt)}
          on={selected.includes(opt)}
          onPress={() => toggle(opt)}
        />
      ))}
      {footer}
      {multi && (
        <Pressable
          onPress={onClose}
          style={(s: any) => [
            FS.doneBtn,
            (s.hovered || s.pressed) && { opacity: 0.9 },
          ]}
        >
          <Text style={FS.doneText}>Done</Text>
        </Pressable>
      )}
    </ResponsiveSheet>
  );
};

// ─── Order row (wide) and card (narrow) ───────────────────────────────────────

const OrderRow: React.FC<{ order: Order; onOpen: () => void }> = React.memo(
  ({ order, onOpen }) => {
    const C = useTokens();
    const OR = useMemo(() => createOR(C), [C]);
    const docs = orderDocs(order);
    return (
      <Pressable
        onPress={onOpen}
        accessibilityRole="button"
        accessibilityLabel={`Open order ${orderTitle(order)}`}
        style={(s: any) => [
          OR.row,
          s.hovered && OR.rowHover,
          s.pressed && { opacity: 0.85 },
        ]}
      >
        <RNView style={[OR.cell, OR.colOrder]}>
          <Text style={OR.orderNo} numberOfLines={1}>
            {orderTitle(order)}
          </Text>
          {!!docs && (
            <Text style={OR.sub} numberOfLines={1}>
              {docs}
            </Text>
          )}
        </RNView>
        <RNView style={[OR.cell, OR.colStatus]}>
          <StatusBadge status={order.orderStatus} C={C} />
        </RNView>
        <RNView style={[OR.cell, OR.colType]}>
          <Text style={OR.text} numberOfLines={1}>
            {order.requestType || "—"}
          </Text>
        </RNView>
        <RNView style={[OR.cell, OR.colGrower]}>
          <Text style={OR.text} numberOfLines={1}>
            {order.subOrganization?.name || "—"}
          </Text>
        </RNView>
        <RNView style={[OR.cell, OR.colBy]}>
          <CreatedBy order={order} C={C} />
        </RNView>
        <RNView style={[OR.cell, OR.colDate]}>
          <Text style={OR.text} numberOfLines={1}>
            {formatDate(order.createdAt)}
          </Text>
        </RNView>
        <FeatherIcon name="chevron-right" size={16} color={C.textMuted} />
      </Pressable>
    );
  },
);

const OrderCard: React.FC<{ order: Order; onOpen: () => void }> = React.memo(
  ({ order, onOpen }) => {
    const C = useTokens();
    const OR = useMemo(() => createOR(C), [C]);
    const docs = orderDocs(order);
    return (
      <Pressable
        onPress={onOpen}
        accessibilityRole="button"
        accessibilityLabel={`Open order ${orderTitle(order)}`}
        style={(s: any) => [OR.card, s.pressed && { opacity: 0.85 }]}
      >
        <RNView style={OR.cardTop}>
          <RNView style={OR.cardIcon}>
            <FeatherIcon name="file-text" size={16} color={C.accent} />
          </RNView>
          <RNView style={{ flex: 1, minWidth: 0 }}>
            <Text style={OR.orderNo} numberOfLines={1}>
              {orderTitle(order)}
            </Text>
            {!!docs && (
              <Text style={OR.sub} numberOfLines={1}>
                {docs}
              </Text>
            )}
          </RNView>
          <StatusBadge status={order.orderStatus} C={C} />
        </RNView>
        <RNView style={OR.cardMeta}>
          <Meta icon="truck" text={order.requestType} C={C} />
          <Meta icon="grid" text={order.subOrganization?.name} C={C} />
          <CreatedBy order={order} compact C={C} />
          <Meta icon="calendar" text={formatDate(order.createdAt)} C={C} />
        </RNView>
      </Pressable>
    );
  },
);

const SkeletonRow = ({ wide, C }: { wide: boolean; C: Tokens }) => {
  const OR = useMemo(() => createOR(C), [C]);
  const block = (w: number | `${number}%`, h: number) => (
    <RNView
      style={{
        width: w,
        height: h,
        borderRadius: 6,
        backgroundColor: C.surfaceAlt,
      }}
    />
  );
  return wide ? (
    <RNView style={OR.row}>
      <RNView style={[OR.cell, OR.colOrder]}>{block("70%", 12)}</RNView>
      <RNView style={[OR.cell, OR.colStatus]}>{block(90, 20)}</RNView>
      <RNView style={[OR.cell, OR.colType]}>{block("70%", 10)}</RNView>
      <RNView style={[OR.cell, OR.colGrower]}>{block("60%", 10)}</RNView>
      <RNView style={[OR.cell, OR.colBy]}>{block("80%", 10)}</RNView>
      <RNView style={[OR.cell, OR.colDate]}>{block("60%", 10)}</RNView>
    </RNView>
  ) : (
    <RNView style={[OR.card, { gap: 10 }]}>
      {block("50%", 14)}
      {block("80%", 10)}
    </RNView>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

interface Props {
  navigation?: any;
  route?: {
    params?: { orgId?: string; orgName?: string; logoUrl?: string };
  };
}

const OrganizationOrdersScreen: React.FC<Props> = ({ navigation, route }) => {
  const C = useTokens();
  const S = useMemo(() => createS(C), [C]);
  const dispatch = useDispatch<any>();
  const isFocused = useIsFocused();
  const paperTheme = usePaperTheme();
  const { colors } = useAppTheme();
  const { width } = useWindowDimensions();
  const isWide = width >= WIDE_BREAKPOINT;

  const orgId = route?.params?.orgId ?? null;
  const orgName = route?.params?.orgName ?? "Organization";

  const {
    orders,
    loading,
    searching,
    totalCount,
    totalPages,
    page,
    pageSize,
    searchQuery,
    categoryFilter,
    sortBy,
    requestType,
    dateFilter,
  } = useSelector((state: any) => state.portal);

  const statuses = useMemo(() => toList(categoryFilter), [categoryFilter]);
  const requestTypes = useMemo(() => toList(requestType), [requestType]);
  const hasFilters =
    statuses.length > 0 ||
    requestTypes.length > 0 ||
    (!!dateFilter && dateFilter !== "all" && dateFilter !== "Bi-Weekly");

  // ── Local UI state ────────────────────────────────────────────────────────
  const [localSearch, setLocalSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [openFilter, setOpenFilter] = useState<FilterKey | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [readyForOrg, setReadyForOrg] = useState<string | null>(null);

  // ── Start clean for this org (the order list is shared with other screens)
  useEffect(() => {
    dispatch(resetOrderQuery());
    setLocalSearch("");
    setReadyForOrg(orgId);
  }, [dispatch, orgId]);

  // ── Fetch on focus and whenever a filter / page changes ───────────────────
  const fetchOrders = useCallback(
    () => (orgId ? dispatch(getOrganizationOrders(orgId)) : Promise.resolve()),
    [dispatch, orgId],
  );

  useEffect(() => {
    if (!isFocused || !orgId || readyForOrg !== orgId) return;
    fetchOrders();
  }, [
    isFocused,
    orgId,
    readyForOrg,
    fetchOrders,
    page,
    searchQuery,
    categoryFilter,
    requestType,
    dateFilter,
    sortBy,
  ]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchOrders();
    } finally {
      setRefreshing(false);
    }
  }, [fetchOrders]);

  // ── Debounced search ──────────────────────────────────────────────────────
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const handleSearchChange = useCallback(
    (text: string) => {
      setLocalSearch(text);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        dispatch(setOrderSearchQuery(text.trim()));
      }, 400);
    },
    [dispatch],
  );

  const clearSearch = useCallback(() => {
    setLocalSearch("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    dispatch(setOrderSearchQuery(""));
  }, [dispatch]);

  // ── Filters ───────────────────────────────────────────────────────────────
  const setStatuses = (list: string[]) =>
    dispatch(setOrderCategoryFilter(fromList(list)));
  const setRequestTypes = (list: string[]) => {
    dispatch(setRequestType(fromList(list)));
    dispatch(setOrderPage(1)); // setRequestType doesn't reset the page
  };
  const setDate = (list: string[]) =>
    dispatch(setOrderDateFilter(list[0] ?? "all"));

  const clearFilters = () => {
    dispatch(setOrderCategoryFilter("all"));
    dispatch(setRequestType("all"));
    dispatch(setOrderDateFilter("Bi-Weekly"));
  };

  const customRange = useMemo(() => {
    if (!dateFilter?.startsWith("Custom:"))
      return { start: undefined, end: undefined };
    const [from, to] = dateFilter.replace("Custom:", "").split("_");
    return { start: new Date(from), end: new Date(to) };
  }, [dateFilter]);

  const handleDateConfirm = ({ startDate, endDate }: any) => {
    setDatePickerOpen(false);
    if (!startDate) return;
    const from = formatLocalDate(startDate);
    const to = formatLocalDate(endDate || startDate);
    dispatch(setOrderDateFilter(`Custom:${from}_${to}`));
  };

  // ── Pagination ────────────────────────────────────────────────────────────
  const goToPage = (p: number) => {
    if (p >= 1 && p <= (totalPages || 1)) dispatch(setOrderPage(p));
  };
  const rangeStart = totalCount ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = totalCount ? Math.min(page * pageSize, totalCount) : 0;

  // ── Navigation ────────────────────────────────────────────────────────────
  const openOrder = useCallback(
    (order: Order) =>
      navigation?.navigate("OrganizationOrderSummaryScreen", {
        orderId: order._id,
        orgId,
        orgName,
      }),
    [navigation, orgId, orgName],
  );

  const renderItem = useCallback(
    ({ item }: { item: Order }) =>
      isWide ? (
        <OrderRow order={item} onOpen={() => openOrder(item)} />
      ) : (
        <OrderCard order={item} onOpen={() => openOrder(item)} />
      ),
    [isWide, openOrder],
  );

  const keyExtractor = useCallback(
    (item: Order, i: number) =>
      String(item._id ?? item.documentNumber ?? item.orderNo ?? i),
    [],
  );

  const firstLoad =
    readyForOrg !== orgId ||
    (loading && !refreshing && !searching && orders.length === 0);
  const busy = (loading || searching) && !refreshing;

  // ── Render ────────────────────────────────────────────────────────────────
  if (!orgId) {
    return (
      <View style={[S.root, S.center]}>
        <Text style={S.emptyTitle}>No organization selected</Text>
        <TouchableOpacity
          style={S.emptyBtn}
          onPress={() => navigation?.goBack?.()}
        >
          <Text style={S.emptyBtnText}>Go back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const FilterButton = ({
    k,
    label,
    value,
    active,
  }: {
    k: FilterKey;
    label: string;
    value: string;
    active: boolean;
  }) => (
    <Pressable
      onPress={() => setOpenFilter(k)}
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${value}`}
      style={(s: any) => [
        S.filterBtn,
        active && S.filterBtnActive,
        (s.hovered || s.pressed) && !active && S.btnHover,
      ]}
    >
      <Text style={S.filterLabel}>{label}</Text>
      <Text
        style={[S.filterValue, active && { color: C.accent }]}
        numberOfLines={1}
      >
        {value}
      </Text>
      <FeatherIcon name="chevron-down" size={13} color={C.textMuted} />
    </Pressable>
  );

  return (
    <View style={S.root}>
      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <RNView style={S.header}>
        <RNView style={S.headerInner}>
          <RNView style={S.titleRow}>
            {navigation?.canGoBack?.() && (
              <Pressable
                onPress={() => navigation.goBack()}
                accessibilityLabel="Back"
                hitSlop={8}
                style={(s: any) => [
                  S.backBtn,
                  (s.hovered || s.pressed) && S.btnHover,
                ]}
              >
                <FeatherIcon name="arrow-left" size={18} color={C.text} />
              </Pressable>
            )}
            <RNView style={S.avatar}>
              <Text style={S.avatarText}>{getInitials(orgName)}</Text>
            </RNView>
            <RNView style={{ flex: 1, minWidth: 0 }}>
              <Text style={S.eyebrow} numberOfLines={1}>
                {orgName}
              </Text>
              <RNView
                style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
              >
                <Text style={S.title}>Orders</Text>
                {busy && !firstLoad && (
                  <ActivityIndicator size="small" color={C.accent} />
                )}
              </RNView>
            </RNView>
            <Pressable
              onPress={handleRefresh}
              accessibilityLabel="Refresh"
              style={(s: any) => [
                S.iconBtn,
                (s.hovered || s.pressed) && S.btnHover,
              ]}
            >
              <FeatherIcon name="refresh-cw" size={15} color={C.textMid} />
            </Pressable>
          </RNView>

          <RNView style={[S.toolbar, isWide && S.toolbarWide]}>
            <RNView
              style={[
                S.searchWrap,
                isWide && S.searchWrapWide,
                searchFocused && S.searchWrapFocused,
              ]}
            >
              <FeatherIcon name="search" size={15} color={C.textMuted} />
              <TextInput
                style={[S.searchInput, webNoOutline]}
                value={localSearch}
                onChangeText={handleSearchChange}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Search by order number"
                placeholderTextColor={C.textMuted}
                returnKeyType="search"
                autoCorrect={false}
                autoCapitalize="none"
              />
              {localSearch.length > 0 && (
                <TouchableOpacity onPress={clearSearch} hitSlop={8}>
                  <FeatherIcon name="x-circle" size={15} color={C.textMuted} />
                </TouchableOpacity>
              )}
            </RNView>

            <RNView style={S.filters}>
              <FilterButton
                k="date"
                label="Date"
                value={dateLabel(dateFilter)}
                active={!!dateFilter && dateFilter !== "all"}
              />
              <FilterButton
                k="status"
                label="Status"
                value={multiLabel(statuses, "All", statusLabel)}
                active={statuses.length > 0}
              />
              <FilterButton
                k="requestType"
                label="Type"
                value={multiLabel(requestTypes, "All")}
                active={requestTypes.length > 0}
              />
              {hasFilters && (
                <Pressable onPress={clearFilters} hitSlop={6}>
                  <Text style={S.clearLink}>Reset</Text>
                </Pressable>
              )}
            </RNView>
          </RNView>
        </RNView>
      </RNView>

      {/* ══ BODY ════════════════════════════════════════════════════════════ */}
      <FlatList<Order>
        data={firstLoad ? [] : orders}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        contentContainerStyle={[S.listContent, !isWide && { gap: 10 }]}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={C.accent}
            colors={[C.accent]}
          />
        }
        ListHeaderComponent={
          <>
            <Text style={S.countText}>
              {firstLoad
                ? "Loading orders…"
                : `${totalCount ?? 0} ${totalCount === 1 ? "order" : "orders"}`}
            </Text>
            {isWide && (
              <RNView style={S.tableHead}>
                {[
                  ["Order", S.colOrder],
                  ["Status", S.colStatus],
                  ["Type", S.colType],
                  ["Grower", S.colGrower],
                  ["Created by", S.colBy],
                  ["Created", S.colDate],
                ].map(([label, style]: any) => (
                  <Text key={label} style={[S.th, style]}>
                    {label}
                  </Text>
                ))}
                <RNView style={{ width: 16 }} />
              </RNView>
            )}
          </>
        }
        ListEmptyComponent={
          firstLoad ? (
            <RNView style={isWide ? S.tableBody : { gap: 10 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonRow key={i} wide={isWide} C={C} />
              ))}
            </RNView>
          ) : (
            <RNView style={S.emptyState}>
              <RNView style={S.emptyIconWrap}>
                <FeatherIcon
                  name={searchQuery || hasFilters ? "search" : "file-text"}
                  size={26}
                  color={C.accent}
                />
              </RNView>
              <Text style={S.emptyTitle}>
                {searchQuery || hasFilters
                  ? "No matching orders"
                  : "No orders yet"}
              </Text>
              <Text style={S.emptySubtitle}>
                {searchQuery || hasFilters
                  ? "Try another order number, or widen the date range and filters."
                  : `Orders placed for ${orgName} will show up here.`}
              </Text>
              {(!!searchQuery || hasFilters) && (
                <TouchableOpacity
                  style={S.emptyBtn}
                  onPress={() => {
                    clearSearch();
                    dispatch(setOrderCategoryFilter("all"));
                    dispatch(setRequestType("all"));
                    dispatch(setOrderDateFilter("all"));
                  }}
                >
                  <Text style={S.emptyBtnText}>Show all orders</Text>
                </TouchableOpacity>
              )}
            </RNView>
          )
        }
        ListFooterComponent={
          !firstLoad && (totalPages ?? 0) > 1 ? (
            <RNView style={[S.pagination, isWide && S.paginationWide]}>
              <Text style={S.rangeText}>
                {rangeStart}–{rangeEnd} of {totalCount}
              </Text>
              <RNView style={S.pageControls}>
                {[
                  ["chevrons-left", 1, page === 1, "First page"],
                  ["chevron-left", page - 1, page === 1, "Previous page"],
                ].map(([icon, target, disabled, label]: any) => (
                  <TouchableOpacity
                    key={icon}
                    style={[S.pageBtn, disabled && S.pageBtnDisabled]}
                    onPress={() => goToPage(target)}
                    disabled={disabled}
                    accessibilityLabel={label}
                  >
                    <FeatherIcon name={icon} size={16} color={C.text} />
                  </TouchableOpacity>
                ))}
                <Text style={S.pageInfo}>
                  <Text style={{ color: C.text, fontWeight: "800" }}>
                    {page}
                  </Text>{" "}
                  / {totalPages}
                </Text>
                {[
                  ["chevron-right", page + 1, page >= totalPages, "Next page"],
                  [
                    "chevrons-right",
                    totalPages,
                    page >= totalPages,
                    "Last page",
                  ],
                ].map(([icon, target, disabled, label]: any) => (
                  <TouchableOpacity
                    key={icon}
                    style={[S.pageBtn, disabled && S.pageBtnDisabled]}
                    onPress={() => goToPage(target)}
                    disabled={disabled}
                    accessibilityLabel={label}
                  >
                    <FeatherIcon name={icon} size={16} color={C.text} />
                  </TouchableOpacity>
                ))}
              </RNView>
            </RNView>
          ) : null
        }
      />

      {/* ══ FILTER SHEETS ═══════════════════════════════════════════════════ */}
      <FilterSheet
        visible={openFilter === "date"}
        title="Date"
        options={DATE_OPTIONS}
        selected={
          dateFilter &&
          dateFilter !== "all" &&
          !dateFilter.startsWith("Custom:")
            ? [dateFilter]
            : []
        }
        multi={false}
        allLabel="Any time"
        onChange={setDate}
        onClose={() => setOpenFilter(null)}
        footer={
          <Pressable
            onPress={() => {
              setOpenFilter(null);
              setDatePickerOpen(true);
            }}
            style={(s: any) => [
              S.customRange,
              dateFilter?.startsWith("Custom:") && S.customRangeActive,
              (s.hovered || s.pressed) && S.btnHover,
            ]}
          >
            <FeatherIcon name="calendar" size={14} color={C.accent} />
            <Text style={S.customRangeText}>
              {dateFilter?.startsWith("Custom:")
                ? dateLabel(dateFilter)
                : "Custom range…"}
            </Text>
          </Pressable>
        }
      />
      <FilterSheet
        visible={openFilter === "status"}
        title="Status"
        options={STATUSES}
        labelOf={statusLabel}
        selected={statuses}
        multi
        allLabel="All statuses"
        onChange={setStatuses}
        onClose={() => setOpenFilter(null)}
      />
      <FilterSheet
        visible={openFilter === "requestType"}
        title="Request type"
        options={REQUEST_TYPES}
        selected={requestTypes}
        multi
        allLabel="All types"
        onChange={setRequestTypes}
        onClose={() => setOpenFilter(null)}
      />

      {/* @ts-ignore */}
      <ThemeProvider
        theme={{
          ...paperTheme,
          colors: { ...paperTheme.colors, primary: colors.primary },
        }}
      >
        <DatePickerModal
          locale="en-AU"
          mode="range"
          visible={datePickerOpen}
          onDismiss={() => setDatePickerOpen(false)}
          startDate={customRange.start}
          endDate={customRange.end}
          onConfirm={handleDateConfirm}
        />
      </ThemeProvider>
    </View>
  );
};

export default OrganizationOrdersScreen;

// ─── Styles ───────────────────────────────────────────────────────────────────

// Table column widths (shared by header, rows and skeletons)
const COLS = {
  colOrder: { flex: 1.4 },
  colStatus: { flex: 1.3 },
  colType: { flex: 1.1 },
  colGrower: { flex: 1.1 },
  colBy: { flex: 1.5 },
  colDate: { flex: 0.9 },
} as const;

const createS = (C: Tokens) =>
  StyleSheet.create({
    root: { flex: 1, backgroundColor: C.surfaceAlt },
    center: { justifyContent: "center", alignItems: "center", gap: 10 },

    header: {
      backgroundColor: C.surface,
      borderBottomWidth: 1,
      borderBottomColor: C.border,
      paddingHorizontal: 16,
      paddingTop: 14,
      paddingBottom: 14,
    },
    headerInner: {
      width: "100%",
      maxWidth: CONTENT_MAX_WIDTH,
      alignSelf: "center",
      gap: 14,
    },
    titleRow: { flexDirection: "row", alignItems: "center", gap: 12 },
    backBtn: {
      width: 36,
      height: 36,
      borderRadius: 10,
      justifyContent: "center",
      alignItems: "center",
    },
    avatar: {
      width: 40,
      height: 40,
      borderRadius: 11,
      backgroundColor: C.accent + "18",
      justifyContent: "center",
      alignItems: "center",
    },
    avatarText: { fontSize: 14, fontWeight: "800", color: C.accent },
    eyebrow: { fontSize: 12, fontWeight: "600", color: C.textMuted },
    title: { fontSize: 22, fontWeight: "800", color: C.text },
    iconBtn: {
      width: 40,
      height: 40,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.surface,
      justifyContent: "center",
      alignItems: "center",
    },
    btnHover: { backgroundColor: C.surfaceAlt },

    toolbar: { gap: 10 },
    toolbarWide: { flexDirection: "row", alignItems: "center" },
    searchWrap: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      backgroundColor: C.surfaceAlt,
      borderRadius: 10,
      borderWidth: 1.5,
      borderColor: "transparent",
      paddingHorizontal: 12,
      height: 40,
    },
    searchWrapWide: { width: 300 },
    searchWrapFocused: { borderColor: C.accent, backgroundColor: C.surface },
    searchInput: { flex: 1, fontSize: 14, color: C.text, paddingVertical: 0 },

    filters: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
    },
    filterBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      height: 40,
      paddingHorizontal: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.surface,
      maxWidth: 260,
    },
    filterBtnActive: {
      borderColor: C.accent + "66",
      backgroundColor: C.accent + "0D",
    },
    filterLabel: { fontSize: 12, fontWeight: "600", color: C.textMuted },
    filterValue: {
      fontSize: 13,
      fontWeight: "700",
      color: C.text,
      flexShrink: 1,
    },
    clearLink: {
      fontSize: 13,
      fontWeight: "700",
      color: C.accent,
      paddingHorizontal: 4,
    },

    listContent: {
      padding: 16,
      paddingBottom: 40,
      flexGrow: 1,
      width: "100%",
      maxWidth: CONTENT_MAX_WIDTH,
      alignSelf: "center",
    },
    countText: {
      fontSize: 12,
      fontWeight: "700",
      color: C.textMuted,
      marginBottom: 10,
    },

    // Table
    tableHead: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 16,
      paddingVertical: 10,
      backgroundColor: C.surface,
      borderWidth: 1,
      borderColor: C.border,
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      gap: 12,
    },
    th: {
      fontSize: 11,
      fontWeight: "800",
      color: C.textMuted,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    tableBody: { gap: 0 },
    ...COLS,

    emptyState: {
      alignItems: "center",
      paddingTop: 70,
      paddingBottom: 30,
      gap: 8,
      paddingHorizontal: 32,
      backgroundColor: C.surface,
      borderWidth: 1,
      borderColor: C.border,
      borderRadius: 12,
    },
    emptyIconWrap: {
      width: 60,
      height: 60,
      borderRadius: 18,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: 6,
    },
    emptyTitle: { fontSize: 17, fontWeight: "800", color: C.text },
    emptySubtitle: {
      fontSize: 13,
      color: C.textMuted,
      textAlign: "center",
      lineHeight: 19,
      maxWidth: 360,
    },
    emptyBtn: {
      marginTop: 8,
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 20,
      backgroundColor: C.accent + "14",
    },
    emptyBtnText: { fontSize: 13, fontWeight: "700", color: C.accent },

    customRange: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      marginTop: 6,
      paddingVertical: 12,
      paddingHorizontal: 10,
      borderRadius: 10,
      borderTopWidth: 1,
      borderTopColor: C.border,
    },
    customRangeActive: { backgroundColor: C.accent + "10" },
    customRangeText: { fontSize: 14, fontWeight: "700", color: C.accent },

    pagination: {
      alignItems: "center",
      gap: 10,
      paddingTop: 18,
      paddingBottom: 8,
    },
    paginationWide: {
      flexDirection: "row",
      justifyContent: "space-between",
    },
    rangeText: { fontSize: 12, color: C.textMuted, fontWeight: "600" },
    pageControls: { flexDirection: "row", alignItems: "center", gap: 8 },
    pageBtn: {
      width: 36,
      height: 36,
      borderRadius: 10,
      backgroundColor: C.surface,
      borderWidth: 1,
      borderColor: C.border,
      justifyContent: "center",
      alignItems: "center",
    },
    pageBtnDisabled: { opacity: 0.35 },
    pageInfo: {
      fontSize: 13,
      fontWeight: "600",
      color: C.textMuted,
      minWidth: 52,
      textAlign: "center",
    },
  });

const createOR = (C: Tokens) =>
  StyleSheet.create({
    // Wide: table row
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingHorizontal: 16,
      paddingVertical: 13,
      backgroundColor: C.surface,
      borderLeftWidth: 1,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: C.border,
    },
    rowHover: { backgroundColor: C.surfaceAlt },
    cell: { minWidth: 0 },
    ...COLS,
    orderNo: { fontSize: 14, fontWeight: "700", color: C.text },
    sub: { fontSize: 11, color: C.textMuted, marginTop: 2 },
    text: { fontSize: 13, color: C.textMid },

    // Narrow: card
    card: {
      backgroundColor: C.surface,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: C.borderLight,
      padding: 14,
      gap: 12,
      shadowColor: C.navy,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.04,
      shadowRadius: 6,
      elevation: 1,
    },
    cardTop: { flexDirection: "row", alignItems: "center", gap: 10 },
    cardIcon: {
      width: 34,
      height: 34,
      borderRadius: 10,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
    },
    cardMeta: {
      flexDirection: "row",
      flexWrap: "wrap",
      columnGap: 14,
      rowGap: 6,
      paddingTop: 10,
      borderTopWidth: 1,
      borderTopColor: C.borderLight,
    },
  });

const createRS = (C: Tokens) =>
  StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: C.overlay },
    backdropCenter: {
      justifyContent: "center",
      alignItems: "center",
      padding: 24,
    },
    backdropBottom: { justifyContent: "flex-end" },
    panel: { backgroundColor: C.surface },
    panelDialog: {
      width: "100%",
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: C.border,
      shadowColor: "#000",
      shadowOpacity: 0.18,
      shadowRadius: 24,
      shadowOffset: { width: 0, height: 12 },
      elevation: 8,
    },
    panelSheet: {
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      paddingHorizontal: 20,
      paddingTop: 12,
      paddingBottom: 28,
    },
    handle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      backgroundColor: C.border,
      alignSelf: "center",
      marginBottom: 14,
    },
  });

const createFS = (C: Tokens) =>
  StyleSheet.create({
    titleRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
    },
    title: { fontSize: 16, fontWeight: "800", color: C.text },
    option: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingVertical: 11,
      paddingHorizontal: 10,
      borderRadius: 10,
    },
    optionHover: { backgroundColor: C.surfaceAlt },
    optionActive: { backgroundColor: C.accent + "10" },
    optionText: { flex: 1, fontSize: 14, fontWeight: "600", color: C.textMid },
    optionTextActive: { color: C.accent, fontWeight: "700" },
    checkbox: {
      width: 18,
      height: 18,
      borderRadius: 5,
      borderWidth: 2,
      borderColor: C.border,
      justifyContent: "center",
      alignItems: "center",
    },
    radio: {
      width: 18,
      height: 18,
      borderRadius: 9,
      borderWidth: 2,
      borderColor: C.border,
      justifyContent: "center",
      alignItems: "center",
    },
    markOn: { borderColor: C.accent, backgroundColor: C.accent },
    radioDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: "#fff",
    },
    doneBtn: {
      marginTop: 12,
      height: 42,
      borderRadius: 10,
      backgroundColor: C.accent,
      justifyContent: "center",
      alignItems: "center",
    },
    doneText: { fontSize: 14, fontWeight: "800", color: "#fff" },
  });
